import React, { useState, useEffect, useContext } from "react";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import NoIndex from "../NoIndex";

// Lucide React Icons
import {
  CheckCircle2,
  XCircle,
  Clock,
  Ban,
  RotateCcw,
  Users,
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  Package,
  Truck,
  Target,
  Mail,
  MessageSquare,
  Globe,
  CreditCard,
  DollarSign,
  Activity,
  AlertTriangle,
  Server,
  Database,
  Cpu,
  HardDrive,
  Wifi,
  Shield,
  Eye,
  MousePointer,
  BarChart3,
  PieChart,
  LineChart,
  RefreshCw
} from "lucide-react";

import {
  LineChart as RechartsLineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Cell,
  RadialBarChart,
  RadialBar,
} from "recharts";
import logoImage from "../../images/Anlook.svg";
import "./AdminDashboard.css";
import MyContext from "../../MyContext";

// Optimized dummy data
const monthlyOrderData = [
  { name: "Jan", orders: 285, revenue: 45500, customers: 142 },
  { name: "Feb", orders: 342, revenue: 58200, customers: 186 },
  { name: "Mar", orders: 298, revenue: 51300, customers: 165 },
  { name: "Apr", orders: 425, revenue: 78900, customers: 235 },
  { name: "May", orders: 398, revenue: 72400, customers: 218 },
  { name: "Jun", orders: 465, revenue: 89200, customers: 267 },
];

const campaignData = [
  { name: "Summer Sale", type: "EMAIL", status: "ACTIVE", opens: 15420, clicks: 2845, roi: 485.6 },
  { name: "Mobile Push", type: "PUSH", status: "COMPLETED", opens: 8965, clicks: 1234, roi: 298.3 },
  { name: "Social Ads", type: "SOCIAL", status: "ACTIVE", opens: 28450, clicks: 4521, roi: 687.9 },
];

const systemMetrics = [
  { name: "API Response", value: 245, unit: "ms", status: "good" },
  { name: "DB Queries/sec", value: 1847, unit: "q/s", status: "good" },
  { name: "CPU Usage", value: 68.5, unit: "%", status: "warning" },
  { name: "Memory", value: 72.8, unit: "%", status: "good" },
];

const ordersByCategory = [
  { name: "Electronics", value: 1245, color: "#3b82f6" },
  { name: "Clothing", value: 892, color: "#10b981" },
  { name: "Home & Garden", value: 654, color: "#f59e0b" },
  { name: "Books", value: 432, color: "#ef4444" },
];

const geographicData = [
  { region: "North India", orders: 1245, revenue: 234500, growth: 12.5 },
  { region: "South India", orders: 986, revenue: 187600, growth: 8.3 },
  { region: "West India", orders: 1156, revenue: 256700, growth: 15.7 },
];

const recentTransactions = [
  { id: "TXN001", customer: "Rajesh Kumar", amount: 2450, status: "completed", method: "UPI" },
  { id: "TXN002", customer: "Priya Sharma", amount: 1200, status: "pending", method: "Card" },
  { id: "TXN003", customer: "Amit Singh", amount: 3600, status: "completed", method: "Wallet" },
];

const StatisticsDashboard = ({ Admin }) => {
  const { baseUrl } = useContext(MyContext);
  const { config } = useContext(MyContext);
  const [Stats, setStats] = useState({});
  const [OrdersChart, setOrdersChart] = useState([]);
  const [Loading, setLoading] = useState(false);
  const [generateQrId, setGenerateQrId] = useState("");
  const [Clients, setClients] = useState([]);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [systemStatus, setSystemStatus] = useState("operational");
  const [refreshing, setRefreshing] = useState(false);
  // state
  const [orderSummary, setOrderSummary] = useState([]);
  const [orderSummaryDate, setOrderSummaryDate] = useState(() => {
    // local yyyy-mm-dd
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });
  const [orderSummaryLoading, setOrderSummaryLoading] = useState(false);
  const getClientOrderSummary = async (dateParam = orderSummaryDate) => {
    setOrderSummaryLoading(true);
    try {
      const payload = {
        // spelling as given by your API contract
        adnminId: String(Admin.pkid),
        date: dateParam,
      };
      const response = await axios.post(
        `${baseUrl}/api/Admin/v1.0/getClientOrderSummaryData`,
        payload,
        config
      );
      if (response.data?.responseCode === "00") {
        setOrderSummary(response.data.response || []);
      } else {
        // toast.error(response.data?.responseMessage || "Failed to fetch order summary");
      }
    } catch (err) {
      console.error("Order Summary error:", err);
      toast.error("Error fetching order summary");
    } finally {
      setOrderSummaryLoading(false);
    }
  };
  useEffect(() => {
    getClientOrderSummary(orderSummaryDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderSummaryDate]);


  // helpers
  const getTypeCount = (types = [], type) =>
    Number(types.find(t => t.orderType === type)?.orderCount || 0);

  const getClientTotal = (types = []) =>
    types.reduce((sum, t) => sum + Number(t.orderCount || 0), 0);


  const generateQrCode = async () => {
    if (!generateQrId.trim()) {
      toast.error("User ID is required!");
      return;
    }
    try {
      const userDetail = {
        userid: generateQrId,
        secret: "string",
        message: "Hi"
      };
      const response = await axios.post(
        `${baseUrl}/api/Whatsapp/v1.0/generateWhatsappQRCode`,
        userDetail,
        config
      );
      if (response.data.responseCode === "00") {
        toast.success("QR Code generated successfully!");
      } else {
        // toast.error(response.data.responseMessage || "Failed to generate QR Code");
      }
    } catch (error) {
      toast.error("Something went wrong!");
      console.error("Error generating QR Code:", error);
    }
  };

  const refreshDashboard = async () => {
    setRefreshing(true);
    await getStatsCall();
    setTimeout(() => setRefreshing(false), 1000);
  };

  useEffect(() => {
    setOrdersChart([
      { name: "Received", value: Stats?.received_orders_count || 0, amount: Stats?.received_orders_amount || 0 },
      { name: "Dispatched", value: Stats?.dispatched_orders_count || 0, amount: Stats?.dispatched_orders_amount || 0 },
      { name: "Completed", value: Stats?.completed_orders_count || 0, amount: Stats?.completed_orders_amount || 0 }
    ]);
  }, [Stats]);

  const getStatsCall = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${baseUrl}/api/Admin/getDashboardAnalytics`,
        {
          adminId: Admin.pkid,
          userid: "string",
          interval: "30",
        },
        config
      );
      if (response.data.responseCode === "00") {
        setStats(response.data.response);
      } else {
        toast.error(response.data.responseMessage);
      }
    } catch (error) {
      // toast.error("Error fetching analytics data");
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStatsCall();
  }, []);

  const FetchClientCall = async () => {
    setLoading(true);
    try {
      const UserDetail = {
        userid: Admin.pkid,
        latitude: latitude,
        longitude: longitude,
      };
      const response = await axios.post(
        `${baseUrl}/api/Admin/getAllClientList`,
        UserDetail,
        config
      );
      if (response.data.responseCode === "00") {
        setClients(response.data.response);
      } else {
        toast.error(response.data.responseMessage);
      }
    } catch (error) {
      toast.error("Error fetching client data");
      console.error("Error updating Admin login:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    FetchClientCall();
  }, []);

  function formatTwoDecimal(value) {
    return Number(value || 0).toFixed(2);
  }

  function formatCurrency(value) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value || 0);
  }

  function formatNumber(value) {
    return new Intl.NumberFormat('en-IN').format(value || 0);
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'good': return '#10b981';
      case 'warning': return '#f59e0b';
      case 'danger': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <div className="modern-admin-dashboard marginRight">
      <NoIndex />
      {/* <ToastContainer /> */}

      {/* Compact Header Section */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="brand-section">
            <img src={logoImage} alt="Logo" className="brand-logo" />
            <div className="brand-text">
              <h1>Admin Dashboard</h1>
              <p>Welcome, {Admin?.name || 'Admin'} •
                <span className={`status-badge ${systemStatus}`}>
                  <Shield className="w-3 h-3" />
                  {systemStatus.toUpperCase()}
                </span>
              </p>
            </div>
          </div>
          <div className="header-actions">
            <button
              onClick={refreshDashboard}
              className={`refresh-button ${refreshing ? 'spinning' : ''}`}
              disabled={refreshing}
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <div className="qr-generator">
              <input
                type="text"
                placeholder="User ID"
                value={generateQrId}
                onChange={(e) => setGenerateQrId(e.target.value)}
                className="qr-input"
              />
              <button onClick={generateQrCode} className="qr-button" disabled={Loading}>
                <MessageSquare className="w-4 h-4" />
                QR
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Dashboard Content */}
      <div className="dashboard-content">
        {/* Compact KPI Section */}
        <div className="kpi-section">
          <div className="kpi-grid">
            <div className="kpi-card success">
              <CheckCircle2 className="kpi-icon" />
              <div className="kpi-content">
                <h3>{formatCurrency(Stats?.success_amount || 1245600)}</h3>
                <p>Successful Payments</p>
                <span className="kpi-count">{formatNumber(Stats?.success_count?.slice(0, 8) || 2847)}</span>
              </div>
            </div>

            <div className="kpi-card failed">
              <XCircle className="kpi-icon" />
              <div className="kpi-content">
                <h3>{formatCurrency(Stats?.failed_amount || 45200)}</h3>
                <p>Failed Payments</p>
                <span className="kpi-count">{formatNumber(Stats?.failed_count?.slice(0, 8) || 156)}</span>
              </div>
            </div>

            <div className="kpi-card pending">
              <Clock className="kpi-icon" />
              <div className="kpi-content">
                <h3>{formatCurrency(Stats?.pending_amount || 89400)}</h3>
                <p>Pending Payments</p>
                <span className="kpi-count">{formatNumber(Stats?.pending_count?.slice(0, 8) || 234)}</span>
              </div>
            </div>

            <div className="kpi-card users">
              <Users className="kpi-icon" />
              <div className="kpi-content">
                <h3>{formatNumber(Stats?.user_count || 15847)}</h3>
                <p>Active Users</p>
                <span className="kpi-count">Total registered</span>
              </div>
            </div>

            <div className="kpi-card orders">
              <ShoppingCart className="kpi-icon" />
              <div className="kpi-content">
                <h3>{formatNumber(2847)}</h3>
                <p>Total Orders</p>
                <span className="kpi-count">This month</span>
              </div>
            </div>

            <div className="kpi-card revenue">
              <DollarSign className="kpi-icon" />
              <div className="kpi-content">
                <h3>₹649</h3>
                <p>Avg Order Value</p>
                <span className="kpi-count">Per order</span>
              </div>
            </div>
          </div>
        </div>

        {/* Modular Analytics Grid */}
        <div className="analytics-section">
          <div className="modular-grid">

            {/* Revenue Trends - Large */}
            <div className="chart-card revenue-chart">
              <div className="card-header">
                <div className="header-left">
                  <BarChart3 className="w-4 h-4" />
                  <h3>Revenue Trends</h3>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={monthlyOrderData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip
                    contentStyle={{ fontSize: '12px', padding: '8px' }}
                    formatter={(value, name) => [
                      name === 'revenue' ? formatCurrency(value) : formatNumber(value),
                      name
                    ]}
                  />
                  <Bar dataKey="revenue" fill="#3b82f6" />
                  <Line type="monotone" dataKey="orders" stroke="#10b981" strokeWidth={2} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Order Categories - Medium */}
            <div className="chart-card categories-chart">
              <div className="card-header">
                <div className="header-left">
                  <PieChart className="w-4 h-4" />
                  <h3>Categories</h3>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={180}>
                <RechartsPieChart>
                  <Pie
                    data={ordersByCategory}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {ordersByCategory.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [formatNumber(value), 'Orders']} />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
                {/* System Health - Medium */}
            <div className="chart-card system-chart">
              <div className="card-header">
                <div className="header-left">
                  <Server className="w-4 h-4" />
                  <h3>System Health</h3>
                </div>
              </div>
              <div className="system-metrics-compact">
                {systemMetrics.map((metric, index) => (
                  <div key={index} className="metric-row-compact">
                    <span className="metric-name">{metric.name}</span>
                    <div className="metric-value-bar">
                      <span className="metric-value" style={{ color: getStatusColor(metric.status) }}>
                        {metric.value}{metric.unit}
                      </span>
                      <div className="metric-bar-mini">
                        <div
                          className="metric-fill-mini"
                          style={{
                            width: `${Math.min(metric.value, 100)}%`,
                            backgroundColor: getStatusColor(metric.status)
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Client Order Summary (replacing Category card) */}
            <div className="admin-order-summary-card admin-chart-card admin-slot-category">
              <div className="admin-card-header">
                <div className="admin-header-left">
                  <BarChart3 className="admin-w-4 admin-h-4" />
                  <h3>Client Order Summary</h3>
                </div>

                <div className="admin-order-summary-actions">
                  <input
                    type="date"
                    className="admin-order-summary-date"
                    value={orderSummaryDate}
                    onChange={(e) => setOrderSummaryDate(e.target.value)}
                  />
                  <button
                    className={`admin-order-summary-refresh ${orderSummaryLoading ? "admin-spinning" : ""}`}
                    onClick={() => getClientOrderSummary(orderSummaryDate)}
                    disabled={orderSummaryLoading}
                    title="Refresh"
                  >
                    <RefreshCw className="admin-w-4 admin-h-4" />
                  </button>
                </div>
              </div>

              <div className="admin-order-summary-list">
                {orderSummaryLoading && (
                  <div className="admin-order-summary-skeleton">
                    <div className="admin-sk-row" />
                    <div className="admin-sk-row" />
                    <div className="admin-sk-row" />
                    <div className="admin-sk-row" />
                  </div>
                )}

                {!orderSummaryLoading && orderSummary.length === 0 && (
                  <div className="admin-order-summary-empty">No summary found for the selected date.</div>
                )}

                {!orderSummaryLoading && orderSummary.length > 0 && (
                  <div className="admin-order-summary-table">
                    {orderSummary.map((c, i) => {
                      const hd = getTypeCount(c.orderTypes, "home_delivery");
                      const tw = getTypeCount(c.orderTypes, "takeaway");
                      const total = getClientTotal(c.orderTypes);
                      const hdPct = total ? Math.round((hd / total) * 100) : 0;
                      const twPct = total ? 100 - hdPct : 0;

                      return (
                        <div className="admin-order-summary-row" key={c.clientName + i}>
                          <div className="admin-os-left">
                            <h4 className="admin-os-client">{c.clientName}</h4>
                            <div className="admin-os-badges">
                              <span className="admin-os-badge admin-hd">
                                <Truck className="admin-w-3 admin-h-3" />
                                Home Delivery: <b>{hd}</b>
                              </span>
                              <span className="admin-os-badge admin-tw">
                                <Package className="admin-w-3 admin-h-3" />
                                Takeaway: <b>{tw}</b>
                              </span>
                            </div>
                          </div>

                          <div className="admin-os-right">
                            <div className="admin-os-total">
                              <span>Total</span>
                              <b>{total}</b>
                            </div>
                            <div className="admin-os-bar">
                              <div className="admin-os-bar-hd" style={{ width: `${hdPct}%` }} />
                              <div className="admin-os-bar-tw" style={{ width: `${twPct}%` }} />
                            </div>
                            <div className="admin-os-legend">
                              <span className="admin-dot admin-hd" /> {hdPct}% HD
                              <span className="admin-sep">•</span>
                              <span className="admin-dot admin-tw" /> {twPct}% TW
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

        
   {/* Geographic Data - Medium */}
            <div className="chart-card geographic-chart">
              <div className="card-header">
                <div className="header-left">
                  <Globe className="w-4 h-4" />
                  <h3>Geographic</h3>
                </div>
              </div>
              <div className="geographic-data-compact">
                {geographicData.map((region, index) => (
                  <div key={index} className="region-row-compact">
                    <div className="region-info-compact">
                      <h4>{region.region}</h4>
                      <span className="region-stats-compact">
                        {formatNumber(region.orders)} orders • {formatCurrency(region.revenue)}
                      </span>
                    </div>
                    <div className={`growth-badge-compact ${region.growth > 10 ? 'high' : 'medium'}`}>
                      <TrendingUp className="w-3 h-3" />
                      {region.growth}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Campaign Performance - Large */}
            <div className="chart-card campaigns-chart">
              <div className="card-header">
                <div className="header-left">
                  <Target className="w-4 h-4" />
                  <h3>Campaigns</h3>
                </div>
              </div>
              <div className="campaign-list-compact">
                {campaignData.map((campaign, index) => (
                  <div key={index} className="campaign-item-compact">
                    <div className="campaign-header-compact">
                      <h4>{campaign.name}</h4>
                      <span className={`status-badge-mini ${campaign.status.toLowerCase()}`}>
                        {campaign.status}
                      </span>
                    </div>
                    <div className="campaign-metrics-compact">
                      <div className="metric-compact">
                        <Eye className="w-3 h-3" />
                        <span>{formatNumber(campaign.opens)}</span>
                      </div>
                      <div className="metric-compact">
                        <MousePointer className="w-3 h-3" />
                        <span>{formatNumber(campaign.clicks)}</span>
                      </div>
                      <div className="metric-compact roi">
                        <TrendingUp className="w-3 h-3" />
                        <span>{campaign.roi}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

         

            {/* Recent Transactions - Medium */}
            <div className="chart-card transactions-chart">
              <div className="card-header">
                <div className="header-left">
                  <CreditCard className="w-4 h-4" />
                  <h3>Recent Transactions</h3>
                </div>
              </div>
              <div className="transactions-list-compact">
                {recentTransactions.map((transaction) => (
                  <div key={transaction.id} className="transaction-item-compact">
                    <div className="transaction-info-compact">
                      <h4>{transaction.customer}</h4>
                      <span className="transaction-amount-compact">
                        {formatCurrency(transaction.amount)}
                      </span>
                    </div>
                    <div className="transaction-meta-compact">
                      <span className={`status-badge-mini ${transaction.status}`}>
                        {transaction.status === 'completed' && <CheckCircle2 className="w-3 h-3" />}
                        {transaction.status === 'pending' && <Clock className="w-3 h-3" />}
                        {transaction.status}
                      </span>
                      <span className="payment-method-compact">{transaction.method}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Pipeline - Large */}
            <div className="chart-card pipeline-chart">
              <div className="card-header">
                <div className="header-left">
                  <Truck className="w-4 h-4" />
                  <h3>Order Pipeline</h3>
                </div>
              </div>
              <div className="order-pipeline-compact">
                <div className="pipeline-stage-compact">
                  <div className="stage-icon-compact received">
                    <Package className="w-4 h-4" />
                  </div>
                  <div className="stage-info-compact">
                    <h4>Received</h4>
                    <p>{formatCurrency(Stats?.received_orders_amount || 245600)}</p>
                    <span>{formatNumber(Stats?.received_orders_count || 456)} orders</span>
                  </div>
                </div>

                <div className="pipeline-stage-compact">
                  <div className="stage-icon-compact dispatched">
                    <Truck className="w-4 h-4" />
                  </div>
                  <div className="stage-info-compact">
                    <h4>Dispatched</h4>
                    <p>{formatCurrency(Stats?.dispatched_orders_amount || 156700)}</p>
                    <span>{formatNumber(Stats?.dispatched_orders_count || 312)} orders</span>
                  </div>
                </div>

                <div className="pipeline-stage-compact">
                  <div className="stage-icon-compact completed">
                    <CheckCircle2 className="w-4 h-4" />
                  </div>
                  <div className="stage-info-compact">
                    <h4>Completed</h4>
                    <p>{formatCurrency(Stats?.completed_orders_amount || 987400)}</p>
                    <span>{formatNumber(Stats?.completed_orders_count || 1456)} orders</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Optimized CSS Styles */}
      <style jsx>{`
        .modern-admin-dashboard {
          background: #f8fafc;
          min-height: 100vh;
          font-family: 'Inter', system-ui, sans-serif;
        }

        /* Compact Header */
        .dashboard-header {
          background: #ffffff;
          border-bottom: 1px solid #e5e7eb;
          padding: 0.75rem 0rem;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
         max-width: 1400px;
          margin: 0 2rem;
        }

        .brand-section {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .brand-logo {
          height: 32px;
          width: auto;
        }

        .brand-text h1 {
          margin: 0;
          font-size: 1.125rem;
          font-weight: 600;
          color: #1f2937;
        }

        .brand-text p {
          margin: 0;
          color: #6b7280;
          font-size: 0.75rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.125rem 0.375rem;
          border-radius: 0.75rem;
          font-size: 0.625rem;
          font-weight: 600;
          background: #d1fae5;
          color: #065f46;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .refresh-button {
          display: flex;
          align-items: center;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          padding: 0.375rem;
          border-radius: 4px;
          cursor: pointer;
          transition: background-color 0.2s;
          color: #374151;
        }

        .refresh-button:hover {
          background: #e5e7eb;
        }

        .refresh-button.spinning svg {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .qr-generator {
          display: flex;
          gap: 0.25rem;
        }

        .qr-input {
          padding: 0.375rem 0.5rem;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 0.75rem;
          width: 100px;
        }

        .qr-button {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 0.375rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        /* Dashboard Content */
        .dashboard-content {
          max-width: 1500px;
          margin: 0 2rem ;
          padding: 1rem;
        }

        /* Compact KPI Section */
        .kpi-section {
          margin-bottom: 1.5rem;
        }

        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .kpi-card {
          background: #ffffff;
          border-radius: 8px;
          padding: 1rem;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          border-left: 3px solid;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          transition: transform 0.2s;
        }

        .kpi-card:hover {
          transform: translateY(-1px);
        }

        .kpi-card.success { border-left-color: #10b981; }
        .kpi-card.failed { border-left-color: #ef4444; }
        .kpi-card.pending { border-left-color: #f59e0b; }
        .kpi-card.users { border-left-color: #3b82f6; }
        .kpi-card.orders { border-left-color: #10b981; }
        .kpi-card.revenue { border-left-color: #f59e0b; }

        .kpi-icon {
          width: 1.5rem;
          height: 1.5rem;
          opacity: 0.7;
        }

        .kpi-content h3 {
          margin: 0 0 0.125rem 0;
          font-size: 1.25rem;
          font-weight: 700;
          color: #1f2937;
        }

        .kpi-content p {
          margin: 0 0 0.125rem 0;
          font-size: 0.75rem;
          color: #6b7280;
          font-weight: 500;
        }

        .kpi-count {
          font-size: 0.625rem;
          color: #9ca3af;
        }

        /* Modular Grid */
        .analytics-section {
          margin-top: 1rem;
        }

        .modular-grid {
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: 1rem;
          grid-auto-rows: minmax(200px, auto);
        }

        .chart-card {
          background: #ffffff;
          border-radius: 8px;
          padding: 1rem;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          transition: box-shadow 0.2s;
        }

        .chart-card:hover {
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .revenue-chart { grid-column: span 8; grid-row: span 2; }
        .categories-chart { grid-column: span 4; }
        .system-chart { grid-column: span 4; }
        .campaigns-chart { grid-column: span 8; }
        .geographic-chart { grid-column: span 4; }
        .transactions-chart { grid-column: span 4; }
        .pipeline-chart { grid-column: span 4; }

        .card-header {
          display: flex;
          align-items: center;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #f3f4f6;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 0.375rem;
        }

        .card-header h3 {
          margin: 0;
          font-size: 0.875rem;
          font-weight: 600;
          color: #1f2937;
        }

        /* System Metrics Compact */
        .system-metrics-compact {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .metric-row-compact {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .metric-name {
          font-size: 0.75rem;
          color: #6b7280;
          font-weight: 500;
        }

        .metric-value-bar {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex: 1;
          max-width: 120px;
        }

        .metric-value {
          font-size: 0.75rem;
          font-weight: 600;
        }

        .metric-bar-mini {
          height: 3px;
          background: #f3f4f6;
          border-radius: 1.5px;
          flex: 1;
          overflow: hidden;
        }

        .metric-fill-mini {
          height: 100%;
          border-radius: 1.5px;
          transition: width 0.3s ease;
        }

        /* Campaign Compact */
        .campaign-list-compact {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .campaign-item-compact {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 0.75rem;
        }

        .campaign-header-compact {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .campaign-header-compact h4 {
          margin: 0;
          font-size: 0.75rem;
          font-weight: 600;
          color: #1f2937;
        }

        .status-badge-mini {
          display: inline-flex;
          align-items: center;
          gap: 0.125rem;
          padding: 0.125rem 0.375rem;
          border-radius: 0.75rem;
          font-size: 0.625rem;
          font-weight: 600;
        }

        .status-badge-mini.active {
          background: #dbeafe;
          color: #1e40af;
        }

        .status-badge-mini.completed {
          background: #d1fae5;
          color: #065f46;
        }

        .status-badge-mini.pending {
          background: #fef3c7;
          color: #92400e;
        }

        .campaign-metrics-compact {
          display: flex;
          gap: 0.75rem;
        }

        .metric-compact {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.625rem;
          color: #6b7280;
        }

        .metric-compact.roi {
          font-weight: 600;
          color: #059669;
        }

        /* Geographic Compact */
        .geographic-data-compact {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .region-row-compact {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
        }

        .region-info-compact h4 {
          margin: 0 0 0.25rem 0;
          font-size: 0.75rem;
          font-weight: 600;
          color: #1f2937;
        }

        .region-stats-compact {
          font-size: 0.625rem;
          color: #6b7280;
        }

        .growth-badge-compact {
          display: flex;
          align-items: center;
          gap: 0.25rem;
          padding: 0.125rem 0.375rem;
          border-radius: 0.75rem;
          font-size: 0.625rem;
          font-weight: 600;
        }

        .growth-badge-compact.high {
          background: #d1fae5;
          color: #065f46;
        }

        .growth-badge-compact.medium {
          background: #fef3c7;
          color: #92400e;
        }

        /* Transactions Compact */
        .transactions-list-compact {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          max-height: 200px;
          overflow-y: auto;
        }

        .transaction-item-compact {
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          padding: 0.5rem;
        }

        .transaction-info-compact {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.25rem;
        }

        .transaction-info-compact h4 {
          margin: 0;
          font-size: 0.75rem;
          font-weight: 600;
          color: #1f2937;
        }

        .transaction-amount-compact {
          font-size: 0.75rem;
          font-weight: 700;
          color: #059669;
        }

        .transaction-meta-compact {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .payment-method-compact {
          font-size: 0.625rem;
          color: #6b7280;
          background: #f3f4f6;
          padding: 0.125rem 0.375rem;
          border-radius: 0.375rem;
        }

        /* Order Pipeline Compact */
        .order-pipeline-compact {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 0.75rem;
        }

        .pipeline-stage-compact {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          text-align: center;
        }

        .stage-icon-compact {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          color: white;
        }

        .stage-icon-compact.received { background: #3b82f6; }
        .stage-icon-compact.dispatched { background: #8b5cf6; }
        .stage-icon-compact.completed { background: #10b981; }

        .stage-info-compact h4 {
          margin: 0 0 0.25rem 0;
          font-size: 0.75rem;
          font-weight: 600;
          color: #1f2937;
        }

        .stage-info-compact p {
          margin: 0 0 0.25rem 0;
          font-size: 0.875rem;
          font-weight: 700;
          color: #059669;
        }

        .stage-info-compact span {
          font-size: 0.625rem;
          color: #6b7280;
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .modular-grid {
            grid-template-columns: repeat(8, 1fr);
          }
          .revenue-chart { grid-column: span 8; }
          .campaigns-chart { grid-column: span 8; }
          .categories-chart,
          .system-chart,
          .geographic-chart,
          .transactions-chart,
          .pipeline-chart { grid-column: span 4; }
        }

        @media (max-width: 768px) {
          .dashboard-content {
            padding: 0.75rem;
          }
          .kpi-grid {
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
            gap: 0.75rem;
          }
          .modular-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 0.75rem;
          }
          .revenue-chart,
          .campaigns-chart,
          .categories-chart,
          .system-chart,
          .geographic-chart,
          .transactions-chart,
          .pipeline-chart { grid-column: span 4; }
          .header-content {
            flex-direction: column;
            gap: 0.75rem;
          }
          .header-actions {
            justify-content: space-between;
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .kpi-grid {
            grid-template-columns: 1fr;
          }
          .modular-grid {
            grid-template-columns: 1fr;
          }
          .revenue-chart,
          .campaigns-chart,
          .categories-chart,
          .system-chart,
          .geographic-chart,
          .transactions-chart,
          .pipeline-chart { grid-column: span 1; }
          .qr-generator {
            flex-direction: column;
          }
        }
          .admin-slot-category { grid-column: span 8; }
@media (max-width: 1279px) { .admin-slot-category { grid-column: span 8; } }
@media (max-width: 1023px) { .admin-slot-category { grid-column: span 12; } }
@media (max-width: 767px)  { .admin-slot-category { grid-column: span 12; } }

/* Card shell (namespaced) */
.admin-order-summary-card { display: flex; flex-direction: column; scrollbar-width: none;  }
.admin-chart-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 14px 12px;
  box-shadow: 0 1px 2px rgba(16,24,40,.04);
  scrollbar-width: none; 
}

/* Header */
.admin-card-header {
  display: flex;
  align-items: center;
  gap: .5rem;
  margin-bottom: .75rem;
}
.admin-header-left { display: flex; align-items: center; gap: .5rem; }
.admin-w-4 { width: 1rem; } .admin-h-4 { height: 1rem; }
.admin-w-3 { width: .75rem; } .admin-h-3 { height: .75rem; }

.admin-order-summary-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: .5rem;
}
.admin-order-summary-date {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: .35rem .55rem;
  font-size: .8rem;
  background: #fff;
}
.admin-order-summary-refresh {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px; width: 32px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  color: #374151;
}
.admin-order-summary-refresh:hover { background: #e5e7eb; }
.admin-order-summary-refresh.admin-spinning svg { animation: admin-spin 1s linear infinite; }
@keyframes admin-spin { to { transform: rotate(360deg); } }

/* Content vertical sizing tuned for a wide card */
.admin-order-summary-list {
  min-height: 220px;          /* taller than the medium tile */
  max-height: 380px;          /* controlled scroll; matches wide tile look */
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: .6rem;
  scrollbar-width: none; 
}

/* Skeleton */
.admin-order-summary-skeleton .admin-sk-row {
  height: 58px;
  border-radius: 10px;
  background: linear-gradient(90deg,#f3f4f6 25%,#e5e7eb 37%,#f3f4f6 63%);
  background-size: 400% 100%;
  animation: admin-os-shimmer 1.2s ease-in-out infinite;
  border: 1px solid #e5e7eb;
}
@keyframes admin-os-shimmer { 0% {background-position: 100% 0;} 100% {background-position: 0 0;} }

.admin-order-summary-empty {
  padding: .85rem;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
  font-size: .8rem;
  color: #6b7280;
  background: #f9fafb;
}

/* Rows – spacey layout to breathe in the wider slot */
.admin-order-summary-table { display: flex; flex-direction: column; gap: .6rem; }
.admin-order-summary-row {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: .9rem;
}

.admin-os-left { min-width: 0; }
.admin-os-client {
  margin: 0 0 .25rem 0;
  font-size: .95rem;
  font-weight: 700;
  color: #111827;
}
.admin-os-badges { display: flex; flex-wrap: wrap; gap: .5rem; }
.admin-os-badge {
  display: inline-flex; align-items: center; gap: .3rem;
  font-size: .75rem;
  background: #f3f4f6; color: #374151;
  padding: .22rem .55rem; border-radius: 999px; border: 1px solid #e5e7eb;
}
.admin-os-badge.admin-hd { background:#e8f3ff; color:#1e40af; border-color:#dbeafe; }
.admin-os-badge.admin-tw { background:#fff7ed; color:#92400e; border-color:#ffedd5; }

.admin-os-right { display: grid; grid-template-columns: auto; gap: .4rem; justify-items: end; }
.admin-os-total { display: flex; align-items: baseline; gap: .45rem; font-size: .8rem; color:#6b7280; }
.admin-os-total b { font-size: 1rem; color:#111827; }

.admin-os-bar {
  width: 320px;               /* fixed visual width works better in a wide slot */
  max-width: 34vw;            /* cap on very wide screens */
  height: 10px;
  border-radius: 999px; overflow: hidden;
  background: #f3f4f6; border: 1px solid #e5e7eb;
}
.admin-os-bar-hd { background:#3b82f6; }
.admin-os-bar-tw { background:#f59e0b; }

.admin-os-legend {
  display: flex; align-items: center; gap: .5rem;
  font-size: .78rem; color:#6b7280;
}
.admin-os-legend .admin-dot { width: 8px; height: 8px; border-radius: 999px; display:inline-block; }
.admin-os-legend .admin-dot.admin-hd { background:#3b82f6; }
.admin-os-legend .admin-dot.admin-tw { background:#f59e0b; }
.admin-os-legend .admin-sep { opacity:.5; }

/* Tablet & below – make bars fill width nicely */
@media (max-width: 1023px) {
  .admin-order-summary-row { grid-template-columns: 1fr; }
  .admin-os-right { justify-items: stretch; }
  .admin-os-bar { width: 100%; max-width: 100%; }
}
      `}</style>
    </div>
  );
};

export default StatisticsDashboard;
